import { AxiosClient } from "./AxiosClient";

const NOTES_BASE = "notes";

const appendIfDefined = (params, key, value) => {
  if (value === undefined || value === null || value === "") return;
  params.append(key, String(value));
};

const buildQuery = (entries = {}) => {
  const params = new URLSearchParams();

  Object.entries(entries).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => appendIfDefined(params, key, item));
      return;
    }
    appendIfDefined(params, key, value);
  });

  const query = params.toString();
  return query ? `?${query}` : "";
};

const handleNotesError = (label, error) => {
  const message =
    error?.response?.data?.error || error?.message || "Unknown notes API error";
  console.error(`${label}:`, message, error);
  throw error;
};

export const listNotes = async ({
  archived = false,
  pinned,
  tags = [],
  page = 1,
  perPage = 50,
} = {}) => {
  try {
    return await AxiosClient.get(
      `${NOTES_BASE}/list${buildQuery({
        archived,
        pinned,
        tag: tags,
        page,
        per_page: perPage,
      })}`,
    );
  } catch (error) {
    handleNotesError("listNotes error", error);
  }
};

export const searchNotes = async ({ q, archived = false, limit = 50 } = {}) => {
  try {
    return await AxiosClient.get(
      `${NOTES_BASE}/search${buildQuery({ q, archived, limit })}`,
    );
  } catch (error) {
    handleNotesError("searchNotes error", error);
  }
};

export const getNote = async (noteId) => {
  try {
    return await AxiosClient.get(`${NOTES_BASE}/get/${encodeURIComponent(noteId)}`);
  } catch (error) {
    handleNotesError("getNote error", error);
  }
};

export const createNote = async ({
  title = "Untitled note",
  body = "",
  tags = [],
  urls = [],
  citations = [],
  pinned = false,
  color = null,
  archived = false,
} = {}) => {
  try {
    return await AxiosClient.post(`${NOTES_BASE}/create`, {
      title,
      body,
      tags,
      urls,
      citations,
      pinned,
      color,
      archived,
    });
  } catch (error) {
    handleNotesError("createNote error", error);
  }
};

export const updateNote = async (noteId, fields = {}) => {
  try {
    return await AxiosClient.put(
      `${NOTES_BASE}/update/${encodeURIComponent(noteId)}`,
      fields,
    );
  } catch (error) {
    handleNotesError("updateNote error", error);
  }
};

export const archiveNote = async (noteId) => {
  try {
    return await AxiosClient.post(
      `${NOTES_BASE}/archive/${encodeURIComponent(noteId)}`,
    );
  } catch (error) {
    handleNotesError("archiveNote error", error);
  }
};

export const unarchiveNote = async (noteId) => {
  try {
    return await AxiosClient.post(
      `${NOTES_BASE}/unarchive/${encodeURIComponent(noteId)}`,
    );
  } catch (error) {
    handleNotesError("unarchiveNote error", error);
  }
};

export const purgeNote = async (noteId, { force = false } = {}) => {
  try {
    return await AxiosClient.delete(
      `${NOTES_BASE}/purge/${encodeURIComponent(noteId)}${buildQuery({ force })}`,
    );
  } catch (error) {
    handleNotesError("purgeNote error", error);
  }
};

export const listNoteTags = async () => {
  try {
    return await AxiosClient.get(`${NOTES_BASE}/tags`);
  } catch (error) {
    handleNotesError("listNoteTags error", error);
  }
};

export const batchUploadNotes = async ({ notes, onConflict = "skip" } = {}) => {
  try {
    return await AxiosClient.post(`${NOTES_BASE}/batchUpload`, {
      notes,
      on_conflict: onConflict,
    });
  } catch (error) {
    handleNotesError("batchUploadNotes error", error);
  }
};

export const batchUploadNoteFiles = async ({ files, onConflict = "skip" } = {}) => {
  try {
    const formData = new FormData();
    Array.from(files || []).forEach((file) => formData.append("file", file));
    formData.append("on_conflict", onConflict);

    return await AxiosClient.post(`${NOTES_BASE}/batchUpload`, formData);
  } catch (error) {
    handleNotesError("batchUploadNoteFiles error", error);
  }
};

export const batchDownloadNotes = async ({
  format = "json",
  includeArchived = false,
} = {}) => {
  try {
    return await AxiosClient.get(
      `${NOTES_BASE}/batchDownload${buildQuery({
        format,
        include_archived: includeArchived,
      })}`,
      { responseType: "blob" },
    );
  } catch (error) {
    handleNotesError("batchDownloadNotes error", error);
  }
};
